'use client';

import React, { useState } from 'react';
import { Search, GripVertical } from 'lucide-react';
import type { SavedPost } from './SchedulerLayout';

interface Props {
  posts: SavedPost[];
  scheduledPostIds: string[];
  draggingPost: SavedPost | null;
  onDragStart: (post: SavedPost) => void;
  onDragEnd: () => void;
}

const platformColors: Record<string, string> = {
  LI: '#0A66C2',
  IG: '#C13584',
  X: '#aaaaaa',
  FB: '#1877F2',
  BLOG: '#FF5F1F',
  TT: '#69C9D0',
};

const statusStyle: Record<string, string> = {
  'Freigegeben': 'text-green-400',
  'Entwurf': 'text-muted',
  'KI-generiert': 'text-accent',
  'Bearbeitet': 'text-blue-400',
  'Geplant': 'text-purple-400',
};

export default function SchedulerPostPanel({ posts, scheduledPostIds, draggingPost, onDragStart, onDragEnd }: Props) {
  const [search, setSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('alle');

  const platforms = ['alle', ...Array.from(new Set(posts.map((p) => p.platform)))];

  const filtered = posts.filter((p) => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchPlatform = filterPlatform === 'alle' || p.platform === filterPlatform;
    return matchSearch && matchPlatform;
  });

  return (
    <div className="w-72 flex-shrink-0 border-r border-border flex flex-col bg-black overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <p className="section-label">Gespeicherte Beiträge</p>
          <span className="mono text-xs text-muted">{posts.length}</span>
        </div>
        <div className="relative mb-3">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            className="input-punk pl-8 py-2 text-xs"
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {platforms.map((plat) => (
            <button
              key={`filter-${plat}`}
              onClick={() => setFilterPlatform(plat)}
              className={`text-xs mono px-2 py-1 rounded border transition-all ${
                filterPlatform === plat
                  ? 'border-accent text-accent bg-accent/8' :'border-border text-muted hover:border-accent/40 hover:text-white'
              }`}
            >
              {plat}
            </button>
          ))}
        </div>
      </div>

      {/* Hint */}
      <div className="px-4 py-2 border-b border-border bg-surface/50">
        <p className="text-xs text-muted leading-relaxed">
          Beitrag auf Kalendertag ziehen, um ihn zu planen.
        </p>
      </div>

      {/* Post list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.map((post) => {
          const isScheduled = scheduledPostIds.includes(post.id);
          const isDragging = draggingPost?.id === post.id;
          return (
            <div
              key={post.id}
              draggable
              onDragStart={() => onDragStart(post)}
              onDragEnd={onDragEnd}
              className={`group flex items-start gap-2 p-3 border-b border-border/50 cursor-grab active:cursor-grabbing transition-all select-none ${
                isDragging ? 'opacity-40 bg-accent/5' : 'hover:bg-surface'
              } ${isScheduled ? 'opacity-60' : ''}`}
            >
              <div className="mt-1 text-muted group-hover:text-accent transition-colors flex-shrink-0">
                <GripVertical size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="mono text-xs font-700 flex-shrink-0"
                    style={{ color: platformColors[post.platform] ?? '#666' }}
                  >
                    {post.platform}
                  </span>
                  <span className="text-xs text-muted truncate">{post.format}</span>
                  {isScheduled && (
                    <span className="ml-auto text-xs mono text-purple-400 flex-shrink-0">●</span>
                  )}
                </div>
                <p className="text-xs font-500 text-white leading-snug line-clamp-2">{post.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-xs mono ${statusStyle[post.status] ?? 'text-muted'}`}>
                    {post.status}
                  </span>
                  <span className="text-xs text-muted-2">·</span>
                  <span className="text-xs text-muted">{post.tone}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
